import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, Link } from 'react-router-dom';  // added Link & useLocation
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';

const Header = ({
  logoImg = '/images/logo.jpg',
  logoText = '',
  separatorImg = '/images/separator.jpg',
}) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isShrunk, setIsShrunk] = React.useState(false);
  const { t, getLocalizedPath } = useLanguage();

  // Build menu items from translations
  const menuItems = [
    { label: t('header.menu.home'), link: getLocalizedPath('/main') },
    { label: t('header.menu.about'), link: getLocalizedPath('/about') },
    { label: t('header.menu.products'), link: getLocalizedPath('/products') },
  //  { label: t('header.menu.odoo'), link: getLocalizedPath('/odoo-solutions') },
    { label: t('header.menu.automotive'), link: getLocalizedPath('/automotive') },
  ];
  
  React.useEffect(() => {
    const handleScroll = () => {
      setIsShrunk(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const mobileMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      PaperProps={{
        sx: { backgroundColor: '#2c2c2c' },
      }}
    >
      {menuItems.map((item) => {
        const isCurrent = location.pathname === item.link;
        return (
          <MenuItem
            key={item.label}
            component={Link}
            to={item.link}
            onClick={handleCloseMenu}
          >
            <Typography sx={{ color: isCurrent ? theme.palette.primary.main : '#fff' }}>
              {item.label}
            </Typography>
          </MenuItem>
        );
      })}
      <MenuItem>
        <LanguageSelector />
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar
      position="fixed"
      color="transparent"
      sx={{
        transition: 'height 0.3s ease',
        height: isShrunk ? 50 : 64,
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default, 
        borderBottom: `${theme.customBorderWeight} solid ${theme.palette.secondary.main}`,
      }}
    >
      <Toolbar
        sx={{
          minHeight: '100%',
          alignItems: 'center',
          padding: 0,
        }}>
        <Box
          sx={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            flexGrow: 1,
            pl: isMobile ? 2 : 0,
          }}
        >     
          <Box
            component="img"
            src={logoImg}
            alt="Logo"
            sx={{
              height: 40,
              width: 'auto',
              mr: logoText ? 1 : 0,
            }}
          />
          {logoText && (
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {logoText}
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {menuItems.map((item, index) => {
              const isCurrent = location.pathname === item.link;
              return (
                <React.Fragment key={item.label}>
                  <Button
                    component={Link}
                    to={item.link}
                    sx={{
                      color: isCurrent
                        ? theme.palette.primary.main
                        : '#fff',
                      textTransform: 'none',
                      fontWeight: isCurrent ? 'bold' : 'normal',
                    }}
                  >
                    {item.label}
                  </Button>
                  {index < menuItems.length - 1 && (
                    <Box
                      component="img"
                      src={separatorImg}
                      alt="separator"
                      sx={{
                        display: { xs: 'none', md: 'inline-block' },
                        width: 'auto',
                        height: 8,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
            <Box
              component="img"
              src={separatorImg}
              alt="separator"
              sx={{
                display: { xs: 'none', md: 'inline-block' },
                width: 'auto',
                height: 8,
              }}
            />
            <LanguageSelector />
          </Box>
        )}
        {isMobile && (
          <>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleOpenMenu}
              sx={{ mx: 1 }}
            >
              <MenuIcon />
            </IconButton>
            {mobileMenu}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;